import React from 'react'
import { Link } from 'react-router'
import { connect } from 'react-redux'
import find from 'lodash/find'
import { Card, CardHeader, CardText, CardActions } from 'material-ui/Card'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn
} from 'material-ui/Table'
import isEqual from 'lodash/isEqual'
import { grey600 } from 'material-ui/styles/colors'
import RaisedButton from 'material-ui/RaisedButton'

import api from '../../../../api'
import { statusName } from '../../../../fieldOptions/orderStatuses'
import { formatCurrency } from '../../../../formatters'
import ResourceDisplay from '../../../resource-display'
import { setSnackbarMessage } from '../../../../actions/snackbar'
import DownloadItemInvoice from './downloadItemInvoice'

const styles = {
  grey: {
    color: grey600
  }
}

const calculateOrderItemTotal = items => items
  .map(item => item.item_price)
  .reduce((total, price) => total + price, 0)

const calculateFlatDiscountTotal = items => items
  .reduce((total, item) => {
    if (item.discount_flat_amount) {
      total += item.discount_flat_amount
    }
    return total
  }, 0)

const calculatePercentDiscountCombined = items => 1 - items
  .reduce((product, item) => {
    if (item.discount_percent_amount) {
      product *= 1 - (item.discount_percent_amount / 100)
    }
    return product
  }, 1)

class OrderSummary extends React.Component<any, any> {
  constructor (props) {
    super(props)
    this.state = {
      isLoaded: false
    }
  }

  componentWillMount () {
    Promise.all([
      this.loadOrderItems(),
      this.loadReservations(),
      this.loadOrderDiscounts(),
      this.loadAccount()
    ]).then(() => {
      this.setState({ isLoaded: true })
    })
  }

  componentWillReceiveProps (nextProps) {
    if (!isEqual(nextProps.order, this.state.order)) {
      this.loadOrderItems()
      this.loadOrderDiscounts()
    }
  }

  loadOrderItems = () => {
    const body = {
      filters: {
        order_id: this.props.order.id
      }
    }

    return api.get('admin/order_items', { body })
      .then(res => res.json())
      .then(orderItems => {
        this.setState({ orderItems })
        return orderItems
      })
  }

  fulfillOrderItems = () => {
    Promise.all(
      this.state.orderItems.map(orderItem => {
        return api.patch(`admin/order_items/${orderItem.id}`, {
          body: { item_status: 'fulfilled' }
        })
      })
    )
      .then(this.loadOrderItems)
      .then(() => {
        this.props.setSnackbarMessage('All order items have been marked as fulfilled')
      })
  }

  loadReservations () {
    const { order } = this.props
    const body = {
      filters: {
        order_id: order.id
      }
    }

    return api.get('admin/session_reservations', { body })
      .then(res => res.json())
      .then(reservations => {
        this.setState({ reservations })
        return reservations
      })
  }

  loadOrderDiscounts () {
    const body = {
      filters: {
        order_id: this.props.order.id
      }
    }

    return api.get('admin/order_discounts', { body })
      .then(res => res.json())
      .then(orderDiscounts => {
        this.setState({ orderDiscounts })
        return orderDiscounts
      })
  }

  loadAccount () {
    return api.get(`admin/accounts/${this.props.order.account_id}`)
      .then(res => res.json())
      .then(account => {
        this.setState({ account })
        return account
      })
  }

  render () {
    if (!this.state.isLoaded) {
      return null
    }

    const { orderItems, reservations, orderDiscounts, account } = this.state
    const { order } = this.props

    const allOrderItemsFulfilled = orderItems.every(item => item.item_status === 'fulfilled')
    const fulfilledOrdersItems = orderItems.filter(item => item.item_status === 'fulfilled')
    const flatDiscountTotal = calculateFlatDiscountTotal(orderDiscounts)
    const percentDiscountCombined = calculatePercentDiscountCombined(orderDiscounts)
    const orderStatus = order.order_status
    const orderSubtotal = orderStatus === 'open' ? calculateOrderItemTotal(orderItems) : calculateOrderItemTotal(fulfilledOrdersItems)
    const percentDiscountTotal = (orderSubtotal - flatDiscountTotal) * percentDiscountCombined
    const orderTotal = orderSubtotal - flatDiscountTotal - percentDiscountTotal
    const amountPaid = order.amount_paid ? order.amount_paid : 0
    const amountRefunded = order.amount_refunded ? order.amount_refunded : 0
    const outstandingBalance = orderTotal - amountPaid + amountRefunded
    const showPercentDiscount = percentDiscountCombined >= 0 && percentDiscountCombined !== 1

    return (
      <Card>
        <CardHeader title='Order Details' />

        <CardText>
          <Table selectable={false}>
            <TableBody displayRowCheckbox={false}>
              <TableRow>
                <TableRowColumn>
                  Account
                </TableRowColumn>
                <TableRowColumn>
                  {account.type === 'user' &&
                    <ResourceDisplay
                      path='admin/users'
                      filters={{ account_id: account.id }}
                      display='name'
                      link={res => `/security/users/${res.id}/edit`}
                    />}
                  {account.type === 'institution' &&
                    <ResourceDisplay
                      path='admin/institutions'
                      filters={{ account_id: account.id }}
                      display='name'
                      link={res => `/membership/institutions/${res.id}/edit`}
                    />}
                </TableRowColumn>
              </TableRow>
              <TableRow>
                <TableRowColumn>
                  Order Status
                </TableRowColumn>
                <TableRowColumn>
                  {statusName(orderStatus)}
                </TableRowColumn>
              </TableRow>
              {(flatDiscountTotal > 0 || percentDiscountCombined < 1) && [
                <TableRow key='order_subtotal'>
                  <TableRowColumn style={styles.grey}>
                    Order Sub-Total
                  </TableRowColumn>
                  <TableRowColumn style={styles.grey}>
                    {formatCurrency(orderSubtotal)}
                  </TableRowColumn>
                </TableRow>,
                (flatDiscountTotal > 0 && <TableRow key='order_discounts'>
                  <TableRowColumn style={styles.grey}>
                    Order Flat Discounts
                  </TableRowColumn>
                  <TableRowColumn style={styles.grey}>
                    {formatCurrency(flatDiscountTotal)}
                  </TableRowColumn>
                </TableRow>),
                (showPercentDiscount && <TableRow key='order_percent_discounts'>
                  <TableRowColumn style={styles.grey}>
                    Percent Discounts
                  </TableRowColumn>
                  <TableRowColumn style={styles.grey}>
                    {`${(100 * percentDiscountCombined).toFixed(2)}%`}
                  </TableRowColumn>
                </TableRow>),
                (showPercentDiscount && <TableRow key='order_discounted_percent'>
                  <TableRowColumn style={styles.grey}>
                    Order Discount from Percentages
                  </TableRowColumn>
                  <TableRowColumn style={styles.grey}>
                    {formatCurrency(percentDiscountTotal)}
                  </TableRowColumn>
                </TableRow>)
              ]}
              {
                orderStatus === 'open' &&
                  <TableRow>
                    <TableRowColumn style={styles.grey}>
                    Order Total
                    </TableRowColumn>
                    <TableRowColumn style={styles.grey}>
                      {formatCurrency(orderTotal)}
                    </TableRowColumn>
                  </TableRow>
              }
              <TableRow>
                <TableRowColumn>
                  Outstanding Balance
                </TableRowColumn>
                <TableRowColumn>
                  {formatCurrency(outstandingBalance)}
                </TableRowColumn>
              </TableRow>
            </TableBody>
          </Table>

          <Card>
            <CardHeader
              title={`Order Items (${orderItems.length})`}
              subtitle='Click to expand'
              actAsExpander
              showExpandableButton
            />
            <CardText expandable>
              <Table selectable={false}>
                <TableHeader adjustForCheckbox={false} displaySelectAll={false}>
                  <TableRow>
                    <TableHeaderColumn>
                      Product
                    </TableHeaderColumn>
                    <TableHeaderColumn>
                      Status
                    </TableHeaderColumn>
                    <TableHeaderColumn>
                      Price
                    </TableHeaderColumn>
                    <TableHeaderColumn>
                      Description
                    </TableHeaderColumn>
                    <TableHeaderColumn>
                      Links
                    </TableHeaderColumn>
                    <TableHeaderColumn>
                      Download Invoice
                    </TableHeaderColumn>
                  </TableRow>
                </TableHeader>
                <TableBody displayRowCheckbox={false}>
                  {orderItems.map(orderItem => {
                    const reservation = find(reservations, { order_item_id: orderItem.id })

                    return (
                      <TableRow key={orderItem.id}>
                        <TableRowColumn>
                          <ResourceDisplay
                            path='admin/products'
                            filters={{ id: orderItem.product_id }}
                            display='name'
                            link={res => `/ecommerce/products/${res.id}/edit`}
                          />
                        </TableRowColumn>
                        <TableRowColumn>
                          {orderItem.item_status}
                        </TableRowColumn>
                        <TableRowColumn>
                          {formatCurrency(orderItem.item_price)}
                        </TableRowColumn>
                        <TableRowColumn>
                          {orderItem.item_description}
                        </TableRowColumn>
                        <TableRowColumn>
                          {reservation && (
                            <Link to={`/events/session-sales/${reservation.session_sale_id}/reservations/${reservation.id}/edit`}>
                              Session Reservation
                            </Link>
                          )}
                        </TableRowColumn>
                        <TableRowColumn style={{ textAlign: 'center' }}>
                          <DownloadItemInvoice item={orderItem} />
                        </TableRowColumn>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </CardText>
          </Card>

          {orderDiscounts && orderDiscounts.length > 0 && (
            <Card>
              <CardHeader
                title={`Order Discounts (${orderDiscounts.length})`}
                subtitle='Click to expand'
                actAsExpander
                showExpandableButton
              />
              <CardText expandable>
                <Table selectable={false}>
                  <TableHeader adjustForCheckbox={false} displaySelectAll={false}>
                    <TableRow>
                      <TableHeaderColumn>
                        Discount Code
                      </TableHeaderColumn>
                      <TableHeaderColumn>
                        Discount Amount
                      </TableHeaderColumn>
                    </TableRow>
                  </TableHeader>
                  <TableBody displayRowCheckbox={false}>
                    {orderDiscounts.map(orderDiscount => {
                      return (
                        <TableRow key={orderDiscount.id}>
                          <TableRowColumn>
                            <ResourceDisplay
                              path='admin/discounts'
                              filters={{ id: orderDiscount.discount_id }}
                              display='code'
                            />
                          </TableRowColumn>
                          <TableRowColumn>
                            {
                              orderDiscount.discount_flat_amount
                                ? formatCurrency(orderDiscount.discount_flat_amount)
                                : `${orderDiscount.discount_percent_amount}%`
                            }
                          </TableRowColumn>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </CardText>
            </Card>
          )}
        </CardText>

        <CardActions>
          <RaisedButton
            label='Fulfill All Order Items'
            onClick={this.fulfillOrderItems}
            primary
            disabled={allOrderItemsFulfilled}
          />
        </CardActions>
      </Card>
    )
  }
}

// @ts-ignore
OrderSummary.propTypes = {
  order: React.PropTypes.object
}

export default connect(
  null,
  { setSnackbarMessage }
)(OrderSummary)
